import { api, writesClient } from './api';

/** Ops control plane: force-logout + sessions/devices, app versions, and
 * admin notifications (with S3-hosted images under notifications/). */

export interface AppVersionRow {
  app: string;
  platform: string;
  min_version: string;
  latest_version: string;
  store_url: string | null;
  message: string | null;
  updated_at: string;
}

export interface AdminNotificationRow {
  id: string;
  title: string;
  body: string;
  image_url: string | null;
  audience: unknown;
  recipient_count: number;
  created_at: string;
  status?: 'pending' | 'sending' | 'sent' | 'failed' | 'cancelled';
  scheduled_at?: string | null;
  delivered_count?: number | null;
  failed_count?: number | null;
}

export interface NotificationTarget {
  type: 'user' | 'users' | 'app' | 'all';
  userIds?: string[];
  app?: 'customer' | 'merchant' | 'rider';
}

export const opsService = {
  // ── Per-user controls (suspend/activate already in usersService) ────────────
  forceLogout: (userId: string) =>
    api.post<{ message: string }>(`/admin/users/${userId}/force-logout`),

  sessions: (userId: string) =>
    api.get<Record<string, unknown>[]>(`/admin/users/${userId}/sessions`),

  devices: (userId: string) =>
    api.get<Record<string, unknown>[]>(`/admin/users/${userId}/devices`),

  // ── App versions ────────────────────────────────────────────────────────────
  appVersions: () => api.get<AppVersionRow[]>('/apps/versions'),

  updateAppVersion: (row: {
    app: string;
    platform: string;
    minVersion?: string;
    latestVersion?: string;
    storeUrl?: string;
    message?: string;
  }) => api.put<{ message: string }>('/admin/app-versions', row),

  // ── Notifications ───────────────────────────────────────────────────────────
  sendNotification: (payload: {
    title: string;
    body: string;
    imageUrl?: string;
    scheduledAt?: string;
    target: NotificationTarget;
  }) => api.post<{ message: string; recipients: number }>('/admin/notifications/send', payload),

  cancelNotification: (id: string) =>
    api.post<{ message: string }>(`/admin/notifications/${id}/cancel`),

  notificationHistory: () => api.get<AdminNotificationRow[]>('/admin/notifications'),

  /** Presign → S3 PUT → public CloudFront URL, category notification_image
   * (S3 prefix notifications/). The URL must be public: FCM fetches tray
   * images itself. */
  uploadNotificationImage: async (file: File): Promise<string> => {
    const presigned = await api.post<{ uploadUrl: string; publicUrl: string }>(
      '/uploads/presign',
      { fileName: file.name, contentType: file.type || 'image/jpeg', category: 'notification_image' },
    );
    const put = await fetch(presigned.uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type || 'image/jpeg' },
      body: file,
    });
    if (!put.ok) throw new Error('Image upload failed');
    return presigned.publicUrl;
  },
};

// writesClient import kept for parity with other services (interceptors preload)
void writesClient;
